import { useTranslation } from 'react-i18next';
import { useCustomerFormatters } from '../../shared/hooks/useCustomerFormatters.js';
import { resolvePromoEventDisplayName } from '../promotions/resolvePromoEventDisplayName.js';
import type { PromoLineAdjustmentDTO } from '../promotions/promoRewardsApi.js';
import type { CheckoutPricingPreviewResult } from './hooks/useCheckoutPricingPreview.js';

export function CheckoutPromoPreview(input: {
  readonly preview: CheckoutPricingPreviewResult | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly baseAmountMinor: number;
  /** Session charge currency — required on charge path (G12; never CZK invent via formatters). */
  readonly currency?: string;
  readonly eventNameById?: ReadonlyMap<string, string>;
}): JSX.Element | null {
  const { t } = useTranslation('customer');
  const { formatPrice: formatPriceRaw } = useCustomerFormatters();
  const formatPrice = (amountMajor: number): string =>
    formatPriceRaw(amountMajor, input.currency);

  if (input.isLoading) {
    return (
      <p className="text-sm text-[var(--color-on-surface-muted)]" data-testid="checkout-promo-preview-loading">
        {t('promo.checkoutPreviewLoading')}
      </p>
    );
  }
  if (input.isError || input.preview == null) {
    return null;
  }

  const { preview } = input;
  const hasPromoDiscount = (preview.promo?.cartDiscountAmount ?? 0) > 0;
  const hasLoyaltyDiscount = (preview.loyalty?.cartDiscountAmount ?? 0) > 0;
  const hasAdjustments = (preview.promo?.adjustments.length ?? 0) > 0;

  if (!hasPromoDiscount && !hasLoyaltyDiscount && !preview.mutualExclusion && !hasAdjustments) {
    return null;
  }

  return (
    <div
      className="flex flex-col gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-3 py-3"
      data-testid="checkout-promo-preview"
    >
      {hasPromoDiscount ? (
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="text-[var(--color-on-surface-muted)]">{t('promo.checkoutDiscountLabel')}</span>
          <span className="font-medium text-[var(--color-on-surface)]">
            −{formatPrice(preview.promo?.cartDiscountAmount ?? 0)}
          </span>
        </div>
      ) : null}
      {hasLoyaltyDiscount ? (
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="text-[var(--color-on-surface-muted)]">{t('promo.checkoutLoyaltyDiscountLabel')}</span>
          <span className="font-medium text-[var(--color-on-surface)]">
            −{formatPrice(preview.loyalty?.cartDiscountAmount ?? 0)}
          </span>
        </div>
      ) : null}
      {(preview.promo?.adjustments.length ?? 0) > 0 ? (
        <ul className="flex flex-col gap-1 border-t border-[var(--color-border)] pt-2 text-xs">
          {preview.promo?.adjustments.map((adjustment: PromoLineAdjustmentDTO) => (
            <li
              key={`${adjustment.lineKey}:${adjustment.eventId}`}
              className="flex items-center justify-between gap-3"
            >
              <span className="text-[var(--color-on-surface-muted)]">
                {t('promo.receiptLine', {
                  eventName: resolvePromoEventDisplayName({
                    eventId: adjustment.eventId,
                    eventNameById: input.eventNameById,
                    fallbackLabel: t('promo.progressEventFallback'),
                  }),
                })}
              </span>
              <span className="text-[var(--color-on-surface)]">
                −{formatPrice(adjustment.discountAmount)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {preview.totalDiscountAmount > 0 ? (
        <div className="flex items-center justify-between gap-3 border-t border-[var(--color-border)] pt-2 text-sm font-semibold">
          <span>{t('promo.checkoutTotalDueLabel')}</span>
          <span data-testid="checkout-promo-preview-total">
            {formatPrice(Math.max(0, input.baseAmountMinor / 100 - preview.totalDiscountAmount))}
          </span>
        </div>
      ) : null}
    </div>
  );
}
